<template>
  <!-- Compact: below `md` there is no room for a column, so the same navigation collapses into
       a single sticky selector that sits directly above the stream. -->
  <div v-if="compact" class="version-rail version-rail--compact">
    <v-select
      :value="activeAnchor"
      :items="flatVersions"
      item-text="label"
      item-value="anchorId"
      dense
      outlined
      hide-details
      label="Jump to version"
      menu-props="offsetY"
      @change="$emit('select', $event)" />
  </div>

  <!-- Full rail: year-grouped version index that scroll-spies the release being read. -->
  <nav v-else ref="rail" class="version-rail" aria-label="Release versions">
    <div class="version-rail__heading">Versions</div>

    <div v-for="group in groups" :key="group.year" class="version-rail__group">
      <div class="version-rail__year">{{ group.year }}</div>
      <ul class="version-rail__list">
        <li v-for="version in group.versions" :key="version.anchorId">
          <button
            :ref="`item-${version.anchorId}`"
            type="button"
            class="version-rail__item"
            :class="{ 'version-rail__item--active': version.anchorId === activeAnchor }"
            :aria-current="version.anchorId === activeAnchor ? 'true' : 'false'"
            @click="$emit('select', version.anchorId)">
            <span class="version-rail__dot" aria-hidden="true"></span>
            <!-- Version name only: the date belongs to the entry itself, and repeating it here
                 crowded the column without helping anyone navigate. -->
            <span class="version-rail__name">{{ version.name }}</span>
          </button>
        </li>
      </ul>
    </div>

    <div v-if="hasMore" class="version-rail__more">
      <v-progress-circular v-if="isLoadingMore" indeterminate size="14" width="2" color="primary" />
      <span v-else>Scroll for earlier releases</span>
    </div>
  </nav>
</template>

<script>
  export default {
    name: 'ReleaseVersionRail',
    props: {
      /**
       * Year-grouped versions, newest group first.
       * @type {Array<{ year: string, versions: Array<{ anchorId, name, fullDate }> }>}
       */
      groups: {
        type: Array,
        default: () => [],
      },
      /** Anchor id of the release currently under the reader (scroll-spy). */
      activeAnchor: {
        type: String,
        default: '',
      },
      /** Render the single-select variant used below the `md` breakpoint. */
      compact: {
        type: Boolean,
        default: false,
      },
      /** More pages are available — the rail says so rather than looking truncated. */
      hasMore: {
        type: Boolean,
        default: false,
      },
      isLoadingMore: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      /**
       * The compact selector flattens the year groups away, so each option carries its own full
       * date; the full rail keeps its year headings and lists version names alone.
       */
      flatVersions() {
        return this.groups.reduce(
          (all, group) =>
            all.concat(
              group.versions.map((version) => ({
                ...version,
                label: version.fullDate ? `${version.name} · ${version.fullDate}` : version.name,
              })),
            ),
          [],
        );
      },
    },
    watch: {
      // A long history scrolls the rail itself; keep the release being read in view, otherwise
      // the highlight silently drifts off the top of the column.
      activeAnchor(anchorId) {
        if (!anchorId || this.compact) return;
        this.$nextTick(() => this.revealActiveItem(anchorId));
      },
    },
    methods: {
      /**
       * Scrolls the rail so the active row is visible, but only when it has actually gone out
       * of view — an unconditional `scrollIntoView` would fight the reader on every tick.
       * @param {string} anchorId
       */
      revealActiveItem(anchorId) {
        const rail = this.$refs.rail;
        const match = this.$refs[`item-${anchorId}`];
        const item = Array.isArray(match) ? match[0] : match;
        if (!rail || !item) return;

        const railBox = rail.getBoundingClientRect();
        const itemBox = item.getBoundingClientRect();
        if (itemBox.top >= railBox.top && itemBox.bottom <= railBox.bottom) return;

        rail.scrollTop += itemBox.top - railBox.top - railBox.height / 3;
      },
    },
  };
</script>

<style scoped lang="scss">
  // The sticky offset and height cap depend on the page around the rail, so they are read from
  // custom properties the host sets. Letting the host override `top` from its own scoped styles
  // instead would pit two equal-specificity selectors against each other, leaving the winner
  // decided by stylesheet order.
  .version-rail {
    position: sticky;
    top: var(--version-rail-top, 0px);
    align-self: start;
    max-height: var(--version-rail-max-height, calc(100vh - 144px));
    overflow-y: auto;
    padding-right: var(--ds-space-sm);
  }

  .version-rail--compact {
    position: sticky;
    top: var(--version-rail-top, 0px);
    z-index: var(--ds-z-sticky);
    max-height: none;
    overflow: visible;
    padding: var(--ds-space-sm) 0 var(--ds-space-md);
    background: var(--v-bg1-base);
  }

  .version-rail__heading {
    margin-bottom: var(--ds-space-md);
    font-size: var(--ds-font-micro);
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--v-text_secondary-base);
  }

  .version-rail__group + .version-rail__group {
    margin-top: var(--ds-space-lg);
  }

  .version-rail__year {
    margin-bottom: var(--ds-space-xs);
    padding-left: var(--ds-space-md);
    font-size: var(--ds-font-caption);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--v-text-base);
    opacity: 0.55;
  }

  .version-rail__list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .version-rail__item {
    display: grid;
    grid-template-columns: 14px minmax(0, 1fr);
    align-items: center;
    gap: var(--ds-space-xs);
    width: 100%;
    padding: 5px var(--ds-space-sm) 5px 0;
    border-left: 2px solid transparent;
    border-radius: 0 var(--ds-radius-sm) var(--ds-radius-sm) 0;
    text-align: left;
    color: var(--v-text_secondary-base);
    transition: color var(--ds-transition-fast), border-color var(--ds-transition-fast);
  }

  .version-rail__item:hover {
    color: var(--v-text-base);
  }

  .version-rail__item:focus-visible {
    outline: var(--ds-focus-ring-width) solid var(--ds-focus-ring-color);
    outline-offset: -2px;
  }

  .version-rail__item--active {
    border-left-color: var(--v-primary-base);
    color: var(--v-primary-base);
  }

  .version-rail__dot {
    justify-self: center;
    width: 5px;
    height: 5px;
    border-radius: var(--ds-radius-full);
    background: currentColor;
    opacity: 0.35;
    transition: opacity var(--ds-transition-fast);
  }

  .version-rail__item--active .version-rail__dot {
    opacity: 1;
  }

  .version-rail__name {
    overflow: hidden;
    font-size: var(--ds-font-body-sm);
    // Versions are numeral-heavy; tabular figures make the column read as an index.
    font-variant-numeric: tabular-nums;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .version-rail__item--active .version-rail__name {
    font-weight: 600;
  }


  .version-rail__more {
    display: flex;
    align-items: center;
    gap: var(--ds-space-sm);
    margin-top: var(--ds-space-lg);
    padding-left: var(--ds-space-md);
    font-size: var(--ds-font-micro);
    color: var(--v-text_secondary-base);
  }

  @media (prefers-reduced-motion: reduce) {
    .version-rail__item,
    .version-rail__dot {
      transition: none;
    }
  }
</style>
